import { useState } from 'react'
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material'

import OrderTrendChart from './OrderTrendChart'
import API from '@aws-amplify/api'
import { DatePicker } from '@material-ui/pickers'
import RevenueChart from './RevenueChart'
import AvgItemCountChart from './AvgItemCountChart'
import UpwardTrajectoryCustomersTable from './UpwardTrajectoryCustomersTable'
import { IInvoice } from 'interfaces/IInvoice'
import { ICustomer } from 'interfaces/ICustomer'

export interface IInvoiceWithCustomer extends IInvoice {
  tblCustomer: ICustomer
}

export const BRANDS = ['BIOLIP', 'DERMABIOME', 'LIPOCEUTICS', 'WLW']
export default function OrderTrendStatistics() {
  const [rawInvoices, setRawInvoices] = useState<any[]>([])
  const [showInHouse, setShowInHouse] = useState(true)
  const [showMaklon, setShowMaklon] = useState(true)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const today = new Date()
  const lastYear = new Date(
    today.getFullYear() - 1,
    today.getMonth(),
    today.getDate()
  )
  const [startDate, setStartDate] = useState<Date | null>(lastYear)
  const [endDate, setEndDate] = useState<Date | null>(today)

  const fetchOrderTrend = async (start?: Date | null, end?: Date | null) => {
    setIsLoading(true)
    setError('')
    try {
      const params: any = {}
      if (start) params.startDate = start.toISOString().slice(0, 10)
      if (end) params.endDate = end.toISOString().slice(0, 10)
      const response: IInvoiceWithCustomer = await API.get(
        'products',
        '/invoices/invoicesByDateRange',
        {
          queryStringParameters: params,
        }
      )
      // Ensure each invoice has a tblCustomer field (for type safety)
      const invoices: IInvoiceWithCustomer[] = Array.isArray(response)
        ? response.filter((inv) => inv.tblCustomer)
        : []
      setRawInvoices(invoices)
    } catch (err) {
      setError('Gagal mengambil data tren order.')
    }
    setIsLoading(false)
  }

  const handleLoad = () => {
    fetchOrderTrend(startDate, endDate)
  }

  // Filter invoices by in-house/maklon toggles
  const filteredInvoices = rawInvoices.filter((inv) => {
    const isInHouse = BRANDS.includes(inv.brandId)
    if (showInHouse && showMaklon) return true
    if (showInHouse) return isInHouse
    if (showMaklon) return !isInHouse
    return false
  })

  return (
    <Paper>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Tren Order
        </Typography>

        <Box display="flex" alignItems="center" mb={2}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            format="yyyy-MM-dd"
            style={{ marginRight: 16 }}
            inputVariant="outlined"
            clearable
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            format="yyyy-MM-dd"
            inputVariant="outlined"
            clearable
          />
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: 16 }}
            onClick={handleLoad}
            disabled={isLoading}
          >
            Load
          </Button>
        </Box>
        <Box mb={2}>
          <label style={{ marginRight: 16 }}>
            <input
              type="checkbox"
              checked={showInHouse}
              onChange={() => setShowInHouse((v) => !v)}
              style={{ marginRight: 4 }}
            />
            Show In House Brands
          </label>
          <label style={{ marginRight: 16 }}>
            <input
              type="checkbox"
              checked={showMaklon}
              onChange={() => setShowMaklon((v) => !v)}
              style={{ marginRight: 4 }}
            />
            Show Maklon Brands
          </label>
        </Box>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <OrderTrendChart invoices={filteredInvoices} />
            <RevenueChart invoices={filteredInvoices} />
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Rata-rata Item per Invoice per Bulan
              </Typography>
              <AvgItemCountChart invoices={filteredInvoices} />
            </Box>
            <UpwardTrajectoryCustomersTable invoices={filteredInvoices} />
          </>
        )}
      </Box>
    </Paper>
  )
}
